import { data } from 'react-router';
import { RootLayout } from '@/navigation/components/RootLayout';
import { HeaderOnlyLayout } from '@/navigation/components/HeaderOnlyLayout';
import { DashboardLayout } from '@/navigation/components/DashboardLayout';
import { RouteErrorBoundary } from '@/navigation/components/RouteErrorBoundary';
import { authLoader } from '@/navigation/loaders/createAuthLoader';
import { lazyRoute } from '@/navigation/utils/lazyRoute';
import { publicRoutes } from './publicRoutes';
import { authRoutes } from './authRoutes';
import { protectedRoutes, headerOnlyRoutes } from './protectedRoutes';
import { dashboardRoutes } from './dashboardRoutes';

// Dev-only route for previewing error pages
const devErrorRoute = import.meta.env.DEV
  ? [
      {
        path: '__dev/error/:code',
        errorElement: <RouteErrorBoundary />,
        loader: ({ params }) => {
          const code = parseInt(params.code, 10) || 500;
          throw data(null, { status: code });
        },
        element: null, // Never renders - loader always throws
      },
    ]
  : [];

/**
 * Main route configuration.
 *
 * Structure:
 * - Auth routes (NO layout, own errorElements) - login, signup, etc.
 * - Root with RootLayout (Header + Footer)
 *   - Public routes (no auth required)
 *   - Protected routes (require auth, use public layout)
 * - Header-only routes (Header, no Footer) - cart, checkout (require auth)
 * - Dashboard with DashboardLayout (Sidebar + NavBar) (require auth)
 * - 404 fallback
 *
 * NavigationProgress component in layouts shows loading state during navigation.
 */
export const routes = [
  // Dev-only error preview route
  ...devErrorRoute,

  // Auth routes - NO layout (minimal UI for login/signup)
  // Spread directly - each route carries its own errorElement (see authRoutes.jsx).
  ...authRoutes,

  // Public layout routes (Header + Footer) - no gate
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    HydrateFallback: null,
    children: [
      ...publicRoutes,
      ...protectedRoutes,
    ],
  },

  // Header-only routes (no footer) - cart, checkout - require auth
  {
    path: '/',
    element: <HeaderOnlyLayout />,
    errorElement: <RouteErrorBoundary />,
    loader: authLoader,
    children: headerOnlyRoutes,
  },

  // Dashboard layout routes (Sidebar + NavBar) - require auth
  {
    path: '/dashboard',
    element: <DashboardLayout />,
    errorElement: <RouteErrorBoundary />,
    loader: authLoader,
    children: dashboardRoutes,
  },

  // 404 Not Found
  {
    path: '*',
    errorElement: <RouteErrorBoundary />,
    lazy: lazyRoute(() => import('@/component/pages/not-found')),
  },
];
